// Auto-generated by fetch-gen
import type { FetchClient, FetchResponse } from '@fgrzl/fetch';
import { buildQueryParams } from '@fgrzl/fetch';

/**
 * Creates an API adapter with typed methods for all OpenAPI operations.
 *
 * @param client - The FetchClient instance to use for HTTP requests
 * @returns An object with typed methods for each API operation
 *
 * @example
 * ```typescript
 * import { createAdapter } from './generated';
 * import client from '@fgrzl/fetch';
 *
 * client.setBaseUrl('https://api.example.com');
 * const api = createAdapter(client);
 *
 * const response = await api.getUsers();
 * if (response.ok) {
 *   console.log(response.data);
 * }
 * ```
 */
export function createAdapter(client: FetchClient): {
  /**
   * Handle SSO callback
   *
   * @param provider - SSO provider name (microsoft or google)
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  ssoCallback: (
    provider: string,
    query?: { code?: string; state?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Renew an agent certificate
   *
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<certRenewResponse>
   */
  renewAgentCertificate: (
    body: certRenewRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<certRenewResponse>>;
  /**
   * Detect available SSO providers
   *
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<string>>
   */
  detectSSOProviders: (
    query?: { email?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<string>>>;
  /**
   * Get dev user token
   *
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<TokenResponse>
   */
  getDevToken: (
    body: DevTokenRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<TokenResponse>>;
  /**
   * Initiate SSO login flow
   *
   * @param provider - SSO provider name (microsoft or google)
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  ssoLogin: (
    provider: string,
    query?: { email?: string; return_url?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Logout user
   *
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  logout: (options?: {
    signal?: AbortSignal;
    timeout?: number;
    operationId?: string;
  }) => Promise<FetchResponse<boolean>>;
  /**
   * Get current user information
   *
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<UserIdentity>
   */
  getCurrentUser: (options?: {
    signal?: AbortSignal;
    timeout?: number;
    operationId?: string;
  }) => Promise<FetchResponse<UserIdentity>>;
  /**
   * Check whether an agent may hold a stream connection
   *
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<connectCheckResponse>
   */
  streamConnectCheck: (
    query?: { tenant_id?: string; client_id?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<connectCheckResponse>>;
  /**
   * Authenticate user for stream access
   *
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<string>
   */
  authenticateStreamUser: (
    body: {
      client_id?: string;
      client_secret?: string;
      scopes?: Array<string>;
      tenant_id?: string;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<string>>;
  /**
   * Get access token
   *
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<TokenResponse>
   */
  getToken: (options?: {
    signal?: AbortSignal;
    timeout?: number;
    operationId?: string;
  }) => Promise<FetchResponse<TokenResponse>>;
} {
  return {
    ssoCallback: (
      provider: string,
      query?: { code?: string; state?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'ssoCallback',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/auth/callback/${encodeURIComponent(String(provider))}` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    renewAgentCertificate: (
      body: certRenewRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<certRenewResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'renewAgentCertificate',
      };
      return client.post(`/auth/cert/renew`, body, undefined, finalOptions);
    },
    detectSSOProviders: (
      query?: { email?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<string>>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'detectSSOProviders',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/auth/detect-provider` + (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    getDevToken: (
      body: DevTokenRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<TokenResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getDevToken',
      };
      return client.post(`/auth/dev/token`, body, undefined, finalOptions);
    },
    ssoLogin: (
      provider: string,
      query?: { email?: string; return_url?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'ssoLogin',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/auth/login/${encodeURIComponent(String(provider))}` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    logout: (options?: {
      signal?: AbortSignal;
      timeout?: number;
      operationId?: string;
    }): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'logout',
      };
      return client.get(`/auth/logout`, undefined, finalOptions);
    },
    getCurrentUser: (options?: {
      signal?: AbortSignal;
      timeout?: number;
      operationId?: string;
    }): Promise<FetchResponse<UserIdentity>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getCurrentUser',
      };
      return client.get(`/auth/me`, undefined, finalOptions);
    },
    streamConnectCheck: (
      query?: { tenant_id?: string; client_id?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<connectCheckResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'streamConnectCheck',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/auth/stream/connect-check` + (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    authenticateStreamUser: (
      body: {
        client_id?: string;
        client_secret?: string;
        scopes?: Array<string>;
        tenant_id?: string;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<string>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'authenticateStreamUser',
      };
      return client.post(`/auth/stream/user`, body, undefined, finalOptions);
    },
    getToken: (options?: {
      signal?: AbortSignal;
      timeout?: number;
      operationId?: string;
    }): Promise<FetchResponse<TokenResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getToken',
      };
      return client.post(`/auth/token`, undefined, undefined, finalOptions);
    },
  };
}

/** DevTokenRequest schema */
export interface DevTokenRequest {
  email: string;
}

/** ProblemDetails schema */
export interface ProblemDetails {
  detail?: string;
  instance?: string;
  status?: number;
  title?: string;
  type?: string;
}

/** TokenResponse schema */
export interface TokenResponse {
  access_token?: string;
  expires_in?: number;
  token_type?: string;
}

/** UserIdentity schema */
export interface UserIdentity {
  email?: string;
  name?: string;
  user_id?: string;
}

/** certRenewRequest schema */
export interface certRenewRequest {
  csr: string;
}

/** certRenewResponse schema */
export interface certRenewResponse {
  ca_certificate_chain?: string;
  certificate?: string;
  certificate_not_after?: string;
}

/** connectCheckResponse schema */
export interface connectCheckResponse {
  allowed?: boolean;
  client_id?: string;
  tenant_id?: string;
}
